import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { AppDefaultInfoContext } from "helpers/globalContext/AppDefaultInfoContext";

import { useTracking } from "utilities/analytics/analytics";

import { isAiSearchStableListEnabled } from "../aiSearchFlags";
import { EXCLUDED_ENTITIES, getEntityLink } from "../AiSearchResults";
import { normalizeSearchId, normalizeTraceId } from "../clickTelemetry";
import { devContextParams, loadDevContext } from "../devContext";
import { getHitKey, normalizeKey } from "../entityMeta";
import { mergeResultsRRF } from "../rrf";
import type {
    EntityScope,
    IAiSearchState,
    ICategorizedHit,
    ISearchResult,
    ITrackedOp,
    SearchStreamEvent,
} from "../types";
import { useAuthToken } from "./useAuthToken";

/**
 * PR-A1 (Search Graft, ADO 286301): how long the stream must stay quiet — no
 * new result events — before the buffered fused list commits to visible state
 * without waiting for `done`. Chosen per SEARCH-UX-CURRENT-AND-TARGET.md §12.5
 * ("~600 ms stability window"): long enough that the keyword/semantic default
 * ops (which land 60-260ms apart) always fuse into one paint, short enough
 * that the user is not left staring at skeletons while the 45s-timeout LLM
 * intent call decides whether to launch extra ops.
 */
const STABILITY_WINDOW_MS = 600;

/**
 * PR-A1-review (thread 1511767): absolute ceiling on how long buffered results
 * may stay invisible while the pending-planned-ops gate holds the stability
 * window open. Armed once, at the first buffered result. If an announced plan
 * dies server-side (per-op crash, timeout) on a stream the server keeps open,
 * nothing else would ever publish the buffer — the window only re-arms on
 * result events. 5s is far past the p95 of the default+extra op pipeline;
 * an op slower than this appends below the committed list instead.
 */
const MAX_UNCOMMITTED_WAIT_MS = 5000;

/** Shared per-op bookkeeping for a result event (both render paths). */
function updateTrackedOps(
    prevOps: ITrackedOp[],
    op: ISearchResult["op"],
    stage: NonNullable<ISearchResult["op"]["stage"]>,
    result: ISearchResult,
): ITrackedOp[] {
    const existing = prevOps.find((o) => o.id === op.id);
    const status: ITrackedOp["status"] = result.error ? "error" : "complete";
    return existing
        ? prevOps.map((o) =>
              o.id === op.id
                  ? {
                        ...o,
                        stage,
                        status,
                        resultCount: result.hits.length,
                        elapsed_ms: result.elapsed_ms,
                    }
                  : o,
          )
        : [
              ...prevOps,
              {
                  ...op,
                  status,
                  resultCount: result.hits.length,
                  elapsed_ms: result.elapsed_ms,
              },
          ];
}

/**
 * PR-A2 (Search Graft, ADO 286302): true when the stream produced only
 * failures — every tracked op reported an error, or no op was ever tracked
 * but per-op error events arrived. Only then does `done` promote the demoted
 * per-op errors back into the fatal `error` state (the red banner).
 * Pending/running ops block the promotion: with an op outcome still unknown,
 * "everything failed" cannot be asserted.
 *
 * A2-review (thread 1511892): the resultCount guard makes "the banner never
 * paints over results" STRUCTURAL instead of dependent on op bookkeeping
 * being complete. An op can report hits AND an error in one result event
 * (partial failure — updateTrackedOps marks it status="error" while its hits
 * merge into the list); without the guard that stream would banner over the
 * very results it delivered.
 *
 * Merge note (A1 integration): promotion runs ONLY on the `done` trigger —
 * commitBuffer's "done" leg and the post-commit done handler. The deadline
 * and stream-end commits (A1 review 1511767/1511970) are terminal WITHOUT
 * the server's verdict, so they resolve done/empty per A1's semantics and
 * never promote: "every op failed" can only be declared by a stream that
 * actually finished telling us about its ops.
 */
function allOpsFailed(ops: ITrackedOp[], opErrors: string[], resultCount: number): boolean {
    if (resultCount > 0) return false; // results on screen — not a total failure
    if (ops.length > 0) return ops.every((op) => op.status === "error");
    return opErrors.length > 0;
}

/**
 * PR-A2: the banner text when `done` finds every op failed — the collected
 * per-op messages when error events carried them, else a generic fallback
 * (covers streams whose failures arrived only as result events with `error`
 * set, never as a standalone `error` event).
 */
function promotedOpErrorMessage(opErrors: string[]): string {
    return opErrors.length > 0 ? opErrors.join(" · ") : "Search failed: no sources responded";
}

/**
 * Hook for SSE-based AI search against the BTNet-Search service.
 *
 * Two rendering contracts, selected by the `aiSearchStableList` flag
 * (aiSearchFlags.ts — read once per mount):
 *
 * OFF (legacy): streams results progressively — every SSE `result` event
 * re-fuses with RRF and repaints the visible list (the 4-8 reshuffles the T4
 * baseline suite pins).
 *
 * ON (PR-A1, buffer-commit-once): `result` events fuse into an INVISIBLE
 * buffer; the list commits to visible state exactly once — at `done`, or when
 * a 600ms stability window elapses with all launched ops complete, whichever
 * fires first. Ops that finish after the commit land in `state.appendedResults`
 * (rendered below the committed list, append-only, never re-sorted into it).
 * Skeletons show until the first commit; a repeat search shows skeletons, not
 * the previous query's results at 30% opacity.
 *
 * Usage:
 *   const { doSearch, state, cancel } = useAiSearch();
 *   doSearch("foundation repair contractors");
 */
export function useAiSearch() {
    const appDefaultInfo = useContext(AppDefaultInfoContext);
    const searchConfig = appDefaultInfo?.searchServiceConfiguration;
    const searchBaseUrl = searchConfig?.apiBaseUrl ?? "";
    const searchAudience = searchConfig?.authAudience ?? "";

    // Read once per mount: rendering must not switch contracts mid-search.
    const [stableListEnabled] = useState(isAiSearchStableListEnabled);

    const { getToken, clearToken } = useAuthToken(searchAudience);
    const { trackEvent } = useTracking({ component: "AI Search" });
    const abortControllerRef = useRef<AbortController | null>(null);
    // Monotonic sequence number bumped on each search/cancel. Used only to
    // discard stale SSE events from a superseded request — unrelated to the
    // telemetry correlation uuid (state.searchId / searchUuidRef).
    const searchSequenceIdRef = useRef(0);
    // The uuid correlation id for the in-flight search. Mirrors state.searchId but
    // is reachable from handleEvent without adding it to the callback's deps.
    const searchUuidRef = useRef("");

    // Refs for entity scope (avoids stale closures in handleEvent)
    const entityScopeRef = useRef<EntityScope>("broad");
    const scopedEntitiesRef = useRef<Set<string>>(new Set());

    // ── PR-A1 stable-list machinery (untouched when the flag is off) ──────
    // The invisible fused list, built by mergeResultsRRF as result events
    // arrive. Published into state.results exactly once by commitBuffer.
    const bufferRef = useRef<ICategorizedHit[]>([]);
    // True once the current search's one-time commit ran. Guards the
    // double-commit race when `done` and the stability window fire together.
    const committedRef = useRef(false);
    // Ops the intent event announced (plans) whose results have not arrived.
    // The stability window refuses to commit while any of these are pending;
    // default ops are only knowable from their result events, so the 600ms of
    // quiet is the heuristic for "no unannounced straggler is imminent".
    const pendingPlannedOpsRef = useRef<Set<string>>(new Set());
    // Ops that have already reported a result. Guards the out-of-order case
    // where an intent's plan lands AFTER its op's result already arrived — the
    // plan must not re-enter pendingPlannedOps and wedge the stability window.
    const completedOpsRef = useRef<Set<string>>(new Set());
    const stabilityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // PR-A1-review (1511767): absolute deadline armed at the FIRST buffered
    // result. The stability window's pending-planned-ops gate can hold the
    // commit open indefinitely if an announced op never reports (per-op crash
    // on a stream the server keeps open) — this timer is the escape hatch
    // that force-publishes the buffer regardless of pending plans.
    const maxWaitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Dead-link hits already reported for the current search. Caps per-search
    // telemetry volume at O(unique dead hits) instead of O(SSE batches × hits),
    // so RRF re-merges of the same hit across batches don't multiply events.
    const reportedDeadLinksRef = useRef<Set<string>>(new Set());

    // Keep trackEvent reachable from handleEvent without forcing its deps to change.
    const trackEventRef = useRef(trackEvent);
    useEffect(() => {
        trackEventRef.current = trackEvent;
    }, [trackEvent]);

    const [state, setState] = useState<IAiSearchState>({
        searchId: "",
        searchQuery: "",
        results: [],
        staleResults: [],
        appendedResults: [],
        committed: false,
        operations: [],
        qaAnswers: {},
        entityScope: "broad",
        scopedEntities: new Set(),
        isQuestion: false,
        phase: "idle",
        intentReceived: false,
        error: null,
        opErrors: [],
        searchStartTime: null,
        traceId: null,
        canonicalSearchId: null,
        staleSearchContext: null,
    });

    const clearStabilityTimer = useCallback(() => {
        if (stabilityTimerRef.current !== null) {
            clearTimeout(stabilityTimerRef.current);
            stabilityTimerRef.current = null;
        }
    }, []);

    const clearMaxWaitTimer = useCallback(() => {
        if (maxWaitTimerRef.current !== null) {
            clearTimeout(maxWaitTimerRef.current);
            maxWaitTimerRef.current = null;
        }
    }, []);

    // Clear both commit timers on unmount so a pending commit can't fire
    // a setState against an unmounted component (V4: timer leak on unmount).
    useEffect(() => {
        return () => {
            clearStabilityTimer();
            clearMaxWaitTimer();
        };
    }, [clearStabilityTimer, clearMaxWaitTimer]);

    /**
     * The one-time commit: publish the buffered fused list into visible state.
     * Four triggers, first one wins (committedRef — not state — is the
     * reentrancy guard, so any two firing in the same tick commit once):
     *   done       — the stream's terminal event; resolves phase to done/empty.
     *   stability  — 600ms of quiet with no announced op pending. The ONLY
     *                non-terminal trigger: the stream is healthy and open, so
     *                loading → streaming and the chrome keeps its honest
     *                "more may come" affordances.
     *   deadline   — MAX_UNCOMMITTED_WAIT_MS after the first buffered result;
     *                overrides the pending-ops gate (review 1511767: a plan
     *                that never reports must not wedge the skeleton forever).
     *                TERMINAL (review 1511970): the deadline means "we've
     *                given up waiting", and its motivating case is a dead op
     *                on a held-open connection — leaving phase "streaming"
     *                there animates the progress bar and "so far..." header
     *                forever. A genuinely-slow op that reports later still
     *                appends: the append path doesn't read phase.
     *   stream-end — the SSE body closed without a done event (connection
     *                drop); nothing more can arrive, so publish what we have.
     */
    const commitBuffer = useCallback(
        (searchSequenceId: number, trigger: "done" | "stability" | "deadline" | "stream-end") => {
            if (searchSequenceId !== searchSequenceIdRef.current) return;
            if (committedRef.current) return;
            committedRef.current = true;
            clearStabilityTimer();
            clearMaxWaitTimer();
            const committedList = bufferRef.current;
            setState((prev) => {
                let phase = prev.phase;
                let error = prev.error;
                if (trigger === "done") {
                    // Terminal with the server's verdict. PR-A2: if EVERY op
                    // failed (and nothing is visible), promote the demoted
                    // per-op errors into the fatal banner; otherwise resolve
                    // done/empty. (Since A2 demoted per-op errors, an SSE
                    // error event no longer sets phase "error", so the old
                    // "done overwrites the error phase" semantic is moot —
                    // fatal transport errors end the stream before done.)
                    if (allOpsFailed(prev.operations, prev.opErrors, committedList.length)) {
                        phase = "error";
                        error = error ?? promotedOpErrorMessage(prev.opErrors);
                    } else {
                        phase = committedList.length > 0 ? "done" : "empty";
                    }
                } else if (trigger === "stream-end" || trigger === "deadline") {
                    // Terminal WITHOUT the server's blessing (dropped
                    // connection / gave up on a dead op). Publish the buffer
                    // and stop the loading chrome, but keep a fatal error
                    // phase visible — unlike `done`, these are not the server
                    // saying "all good". PR-A2 merge note: NO promotion here —
                    // "all ops failed" is only declarable by a stream that
                    // finished reporting (the done trigger); a dead op on a
                    // dropped connection is not a verdict.
                    if (prev.phase !== "error") {
                        phase = committedList.length > 0 ? "done" : "empty";
                    }
                } else if (prev.phase === "loading") {
                    // A stability commit leaves the stream open: loading
                    // becomes streaming. A fatal error phase (auth/transport)
                    // is preserved.
                    phase = "streaming";
                }
                return {
                    ...prev,
                    results: committedList,
                    committed: true,
                    staleResults: [],
                    // Lockstep invariant (A3): no stale rows ⇒ no stale
                    // context. Always already null on the stable path, but
                    // kept mechanical rather than assumed.
                    staleSearchContext: null,
                    phase,
                    error,
                };
            });
        },
        [clearStabilityTimer, clearMaxWaitTimer],
    );

    /**
     * (Re)arm the stability window. Called after every buffered result event,
     * so the window measures quiet time since the LAST result. When it fires
     * it commits only if nothing announced is still running — a planned op's
     * own result event re-arms the window when it lands.
     */
    const armStabilityTimer = useCallback(
        (searchSequenceId: number) => {
            clearStabilityTimer();
            stabilityTimerRef.current = setTimeout(() => {
                stabilityTimerRef.current = null;
                if (searchSequenceId !== searchSequenceIdRef.current) return;
                if (committedRef.current) return;
                if (pendingPlannedOpsRef.current.size > 0) return;
                commitBuffer(searchSequenceId, "stability");
            }, STABILITY_WINDOW_MS);
        },
        [clearStabilityTimer, commitBuffer],
    );

    const handleEvent = useCallback(
        (event: SearchStreamEvent, searchSequenceId: number) => {
            if (searchSequenceId !== searchSequenceIdRef.current) return;

            switch (event.type) {
                case "intent": {
                    const scope = event.entity_scope ?? "broad";
                    const scoped = new Set<string>(event.scoped_entities ?? []);

                    entityScopeRef.current = scope;
                    scopedEntitiesRef.current = scoped;

                    if (stableListEnabled && !committedRef.current && Array.isArray(event.plans)) {
                        // Pre-commit plans gate the stability window: the
                        // window must not commit while an announced op is
                        // still running. Post-commit plans need no gate —
                        // their results append below the committed list.
                        for (const plan of event.plans) {
                            if (!completedOpsRef.current.has(plan.id)) {
                                pendingPlannedOpsRef.current.add(plan.id);
                            }
                        }
                    }

                    setState((prev) => ({
                        ...prev,
                        intentReceived: true,
                        entityScope: scope,
                        scopedEntities: scoped,
                        isQuestion: event.is_question === true,
                        operations: [
                            ...prev.operations,
                            ...(Array.isArray(event.plans)
                                ? event.plans.map(
                                      (plan): ITrackedOp => ({
                                          ...plan,
                                          stage: "extra" as const,
                                          status: "pending" as const,
                                      }),
                                  )
                                : []),
                        ],
                    }));
                    break;
                }

                case "result": {
                    const resultData = event.results;
                    if (!resultData) break;

                    const stage = event.stage ?? "default";
                    const op = { ...resultData.op, stage };
                    const result = { ...resultData, op };

                    // Tracks hits the frontend can't route to (missing payload fields
                    // or unhandled entity types) so we can monitor the backend payload
                    // gap. Dedupes per search to prevent multiple events for the same hit.
                    for (const hit of result.hits) {
                        if (EXCLUDED_ENTITIES.has(normalizeKey(hit.entity))) continue;
                        if (getEntityLink(hit) !== null) continue;
                        const key = getHitKey(hit);
                        if (reportedDeadLinksRef.current.has(key)) continue;
                        reportedDeadLinksRef.current.add(key);
                        trackEventRef.current({
                            event: "Search",
                            extraInfo: {
                                searchId: searchUuidRef.current,
                                searchPhase: "dead-link-dropped",
                                entity: hit.entity,
                                id: hit.id,
                                payloadKeys: Object.keys(hit.payload ?? {}),
                            },
                        });
                    }

                    if (!stableListEnabled) {
                        // Legacy path: fuse and repaint on every result event.
                        setState((prev) => {
                            const nextPhase = prev.phase === "loading" ? "streaming" : prev.phase;
                            return {
                                ...prev,
                                phase: nextPhase,
                                staleResults: [],
                                // A3: stale rows leave the screen here, so the
                                // previous search's correlation ids go with them.
                                staleSearchContext: null,
                                operations: updateTrackedOps(prev.operations, op, stage, result),
                                results: mergeResultsRRF(
                                    prev.results,
                                    result,
                                    entityScopeRef.current,
                                    scopedEntitiesRef.current,
                                ),
                            };
                        });
                        break;
                    }

                    // Stable path: this op is no longer pending (whether it
                    // succeeded or errored, it has reported).
                    pendingPlannedOpsRef.current.delete(op.id);
                    completedOpsRef.current.add(op.id);

                    if (!committedRef.current) {
                        // Buffer invisibly; phase stays "loading" so the
                        // component keeps showing skeletons until the commit.
                        bufferRef.current = mergeResultsRRF(
                            bufferRef.current,
                            result,
                            entityScopeRef.current,
                            scopedEntitiesRef.current,
                        );
                        armStabilityTimer(searchSequenceId);
                        // Absolute deadline, armed ONCE at the first buffered
                        // result and never re-armed: if the pending-planned-ops
                        // gate is still blocking the stability window when it
                        // fires (an announced op died server-side), publish
                        // the buffer anyway (review 1511767).
                        if (maxWaitTimerRef.current === null) {
                            maxWaitTimerRef.current = setTimeout(() => {
                                maxWaitTimerRef.current = null;
                                commitBuffer(searchSequenceId, "deadline");
                            }, MAX_UNCOMMITTED_WAIT_MS);
                        }
                        setState((prev) => ({
                            ...prev,
                            operations: updateTrackedOps(prev.operations, op, stage, result),
                        }));
                    } else {
                        // Late op after the commit: append its NEW hits below
                        // the committed list, in the op's own rank order.
                        // Never re-fused, never re-sorted into the committed
                        // rows; hits already visible (committed or previously
                        // appended) are dropped rather than moved.
                        setState((prev) => {
                            const seen = new Set<string>([
                                ...prev.results.map(getHitKey),
                                ...prev.appendedResults.map(getHitKey),
                            ]);
                            const freshHits: ICategorizedHit[] = result.hits
                                .filter((hit) => !seen.has(getHitKey(hit)))
                                .map((hit) => ({ ...hit, sourceOps: [op] }));
                            // Review 1512051: a deadline/stream-end commit of
                            // an EMPTY buffer resolves phase "empty", but the
                            // stream can still deliver a late op with hits.
                            // Promote empty → done when fresh hits append, so
                            // phase keeps meaning "nothing visible" and the
                            // "No results" panel can never sit above a
                            // populated More-results group. Mirrors the
                            // post-commit `done` handler, which already
                            // recomputes empty vs done from both sections.
                            const phase =
                                freshHits.length > 0 && prev.phase === "empty"
                                    ? "done"
                                    : prev.phase;
                            return {
                                ...prev,
                                phase,
                                operations: updateTrackedOps(prev.operations, op, stage, result),
                                appendedResults:
                                    freshHits.length > 0
                                        ? [...prev.appendedResults, ...freshHits]
                                        : prev.appendedResults,
                            };
                        });
                    }
                    break;
                }

                case "qa": {
                    const source = event.source;
                    const answer = {
                        ...event.answer,
                        source,
                        _elapsed: event._elapsed,
                    };
                    setState((prev) => ({
                        ...prev,
                        qaAnswers: { ...prev.qaAnswers, [source]: answer },
                    }));
                    break;
                }

                case "trace": {
                    // Server correlation ids (BTNet-Search PR 110268): the OTel
                    // trace id for this search plus the canonical (sanitized)
                    // echo of the search_id we sent. `client_search_id` is the
                    // legacy alias of the same echo — server builds that predate
                    // the canonical field send only it, so it backstops the
                    // canonical resolution (both run through the sanitizer
                    // pattern; malformed values fold to null so click telemetry
                    // can rely on stored values being contract-valid). This event
                    // may never arrive; clicks are emitted regardless
                    // (trace_id null).
                    const traceId = normalizeTraceId(event.trace_id);
                    const canonicalSearchId =
                        normalizeSearchId(event.search_id) ??
                        normalizeSearchId(event.client_search_id);
                    // A trace event carrying nothing usable (malformed or
                    // absent ids) is a strict no-op — no state transition, no
                    // render. The T4 baseline suite pins state identity across
                    // such events (fixtures use non-hex trace ids).
                    if (traceId === null && canonicalSearchId === null) break;
                    setState((prev) => ({
                        ...prev,
                        traceId: traceId ?? prev.traceId,
                        canonicalSearchId: canonicalSearchId ?? prev.canonicalSearchId,
                    }));
                    break;
                }

                case "error": {
                    // PR-A2 (both flag states): a per-op error event no longer
                    // flips the whole search to phase "error" — one failed
                    // sub-op must not paint a red banner over healthy results.
                    // The message collects into opErrors (the dismissible
                    // footer note); `done` promotes to the fatal banner only
                    // if ALL ops turn out to have failed.
                    const message = event.error ?? "Search error";
                    setState((prev) => ({
                        ...prev,
                        opErrors: prev.opErrors.includes(message)
                            ? prev.opErrors
                            : [...prev.opErrors, message],
                    }));
                    break;
                }

                case "done": {
                    // PR-A2 (both flag states): if EVERY op failed (and no
                    // results are visible), done promotes the demoted per-op
                    // errors into the fatal banner instead of resolving to
                    // done/empty. Runs ONLY here and in commitBuffer's done
                    // leg — never on the deadline/stream-end commits.
                    const resolveDone = (
                        prev: IAiSearchState,
                        resultCount: number,
                    ): Pick<IAiSearchState, "phase" | "error"> => {
                        if (allOpsFailed(prev.operations, prev.opErrors, resultCount)) {
                            return {
                                phase: "error",
                                error: prev.error ?? promotedOpErrorMessage(prev.opErrors),
                            };
                        }
                        return {
                            phase: resultCount > 0 ? "done" : "empty",
                            error: prev.error,
                        };
                    };
                    if (stableListEnabled) {
                        if (!committedRef.current) {
                            commitBuffer(searchSequenceId, "done");
                        } else {
                            setState((prev) => ({
                                ...prev,
                                staleResults: [],
                                // A3: stale attribution context clears in
                                // lockstep with staleResults.
                                staleSearchContext: null,
                                ...resolveDone(
                                    prev,
                                    prev.results.length + prev.appendedResults.length,
                                ),
                            }));
                        }
                        break;
                    }
                    setState((prev) => ({
                        ...prev,
                        staleResults: [],
                        staleSearchContext: null,
                        ...resolveDone(prev, prev.results.length),
                    }));
                    break;
                }
            }
        },
        [stableListEnabled, armStabilityTimer, commitBuffer],
    );

    const fetchSSE = useCallback(
        async (query: string, signal: AbortSignal, searchSequenceId: number) => {
            let token: string;
            try {
                token = await getToken();
            } catch {
                if (searchSequenceId !== searchSequenceIdRef.current) return;
                setState((prev) => ({
                    ...prev,
                    error: "Failed to authenticate with search service",
                    phase: "error",
                }));
                return;
            }

            try {
                // Dev-only builder/user override (empty string everywhere else;
                // the service honors these params only under AUTH_DISABLED).
                const devParams = devContextParams(loadDevContext());
                const response = await fetch(`${searchBaseUrl}v1/search${devParams}`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ query, search_id: searchUuidRef.current }),
                    signal,
                });

                if (searchSequenceId !== searchSequenceIdRef.current) return;

                if (response.status === 401) {
                    clearToken();
                    setState((prev) => ({
                        ...prev,
                        error: "Authentication expired. Please try again.",
                        phase: "error",
                    }));
                    return;
                }

                if (!response.ok || !response.body) {
                    setState((prev) => ({
                        ...prev,
                        error: `Search failed (${response.status})`,
                        phase: "error",
                    }));
                    return;
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = "";
                let currentEvent = "";

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) {
                        // Stream closed. If the server never sent `done` (the
                        // connection dropped, or every remaining op crashed),
                        // nothing else can arrive — publish whatever is
                        // buffered instead of skeleton-ing forever (review
                        // 1511767). No-op when a done event already committed,
                        // and a no-op on the legacy path (buffer unused).
                        if (stableListEnabled) {
                            commitBuffer(searchSequenceId, "stream-end");
                        }
                        break;
                    }

                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split("\n");
                    buffer = lines.pop()!;

                    for (const line of lines) {
                        if (line.startsWith("event: ")) {
                            currentEvent = line.slice(7).trim();
                        } else if (line.startsWith("data: ")) {
                            try {
                                const eventData = JSON.parse(line.slice(6)) as Record<
                                    string,
                                    unknown
                                >;
                                handleEvent(
                                    {
                                        type: currentEvent,
                                        ...eventData,
                                    } as SearchStreamEvent,
                                    searchSequenceId,
                                );
                            } catch {
                                // Skip malformed JSON
                            }
                            currentEvent = "";
                        }
                    }
                }
            } catch (err: unknown) {
                if (err instanceof DOMException && err.name === "AbortError") {
                    return;
                }
                if (searchSequenceId !== searchSequenceIdRef.current) return;
                setState((prev) => ({
                    ...prev,
                    error: "Connection to search service failed",
                    phase: "error",
                }));
            }
        },
        [searchBaseUrl, getToken, clearToken, handleEvent, stableListEnabled, commitBuffer],
    );

    const doSearch = useCallback(
        (query: string) => {
            if (!searchBaseUrl) {
                setState((prev) => ({
                    ...prev,
                    error: "Search service URL not configured",
                }));
                return;
            }

            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            const controller = new AbortController();
            abortControllerRef.current = controller;

            searchSequenceIdRef.current += 1;
            const searchSequenceId = searchSequenceIdRef.current;

            const searchUuid = uuidv4();
            searchUuidRef.current = searchUuid;

            entityScopeRef.current = "broad";
            scopedEntitiesRef.current = new Set();
            reportedDeadLinksRef.current = new Set();

            // Reset the stable-list machinery for the new stream (no-ops when
            // the flag is off: the refs stay at their initial values).
            clearStabilityTimer();
            clearMaxWaitTimer();
            bufferRef.current = [];
            committedRef.current = false;
            pendingPlannedOpsRef.current = new Set();
            completedOpsRef.current = new Set();

            setState((prev) => ({
                searchId: searchUuid,
                searchQuery: query,
                results: [],
                // Stable path: a repeat search shows skeletons, never the
                // previous query's results at 30% opacity (PR-A1 contract).
                staleResults: stableListEnabled ? [] : prev.results,
                // A3: keep the OLD search's correlation ids alongside its
                // stale rows — a click on a stale row belongs to the search
                // that produced it, not the one now loading. On the stable
                // path no stale rows render, so no context is kept either
                // (staleSearchContext must stay in lockstep with staleResults).
                staleSearchContext:
                    !stableListEnabled && prev.results.length > 0 && prev.searchId
                        ? {
                              searchId: prev.canonicalSearchId ?? prev.searchId,
                              traceId: prev.traceId,
                              query: prev.searchQuery,
                          }
                        : null,
                appendedResults: [],
                committed: false,
                operations: [],
                qaAnswers: {},
                entityScope: "broad",
                scopedEntities: new Set(),
                isQuestion: false,
                phase: "loading",
                intentReceived: false,
                error: null,
                opErrors: [],
                searchStartTime: Date.now(),
                traceId: null,
                canonicalSearchId: null,
            }));

            void fetchSSE(query, controller.signal, searchSequenceId);
        },
        [searchBaseUrl, fetchSSE, stableListEnabled, clearStabilityTimer, clearMaxWaitTimer],
    );

    const cancel = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        // Invalidate any late SSE events still flushing through handleEvent
        // so they can't setState({phase: "streaming"}) after we've gone idle.
        searchSequenceIdRef.current += 1;
        // A pending stability window or max-wait deadline must not commit an
        // aborted search's buffer (abort-mid-stream leaves no partial commit).
        clearStabilityTimer();
        clearMaxWaitTimer();
        bufferRef.current = [];
        // A3: traceId/canonicalSearchId are intentionally KEPT: any visible
        // results still belong to the aborted search, so a click on them must
        // carry that search's correlation ids (trace_id stays null if the
        // trace event never arrived before the abort).
        setState((prev) => ({
            ...prev,
            staleResults: [],
            staleSearchContext: null,
            phase: "idle",
        }));
    }, [clearStabilityTimer, clearMaxWaitTimer]);

    return {
        doSearch,
        cancel,
        state,
        /**
         * Which rendering contract this mount is on (review 1511771: this is
         * deliberately part of the hook's contract, not inferrable from state
         * shape). During pre-commit streaming BOTH contracts have
         * `committed === false` and empty `appendedResults`, yet the view must
         * render differently (skeletons vs per-event paints) and announce the
         * result count differently (PR-A2: once per commit vs once per paint).
         * A state-shape-only view cannot make either choice correctly, so the
         * flag is surfaced ONCE here — consumers must branch on it rather
         * than re-reading isAiSearchStableListEnabled(), which could disagree
         * mid-session (this value is latched at mount). (The keyboard
         * highlight no longer differs by contract: PR-A2 relocates it by hit
         * key on both paths, resetting only on list identity.)
         */
        stableListEnabled,
    };
}
